// Tiny audio + haptics utility for race feedback (checkpoint pass,
// finish geofence hit, etc). We keep a single AudioContext and use
// oscillators so the app doesn't ship any audio assets.
//
// On a native wrapper (Capacitor) `vibratePattern` is proxied to the real
// device haptics engine via `lib/native.haptic()` so haptics work on iOS
// too (Safari ignores navigator.vibrate entirely).

import { haptic as nativeHaptic, isNative } from './native';

let ctxRef = null;

function getCtx() {
  if (typeof window === 'undefined') return null;
  if (ctxRef) return ctxRef;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    ctxRef = new AC();
  } catch {
    return null;
  }
  return ctxRef;
}

// Two-note ascending chime for a "checkpoint passed" cue. Non-blocking.
export function playCheckpointChime() {
  const ctx = getCtx();
  if (!ctx) return;
  // Some browsers suspend the context until a user gesture — try to resume
  // (no-op if already running).
  if (ctx.state === 'suspended') { try { ctx.resume(); } catch { /* noop */ } }
  const now = ctx.currentTime;
  const notes = [
    { freq: 660, at: 0, dur: 0.11 },
    { freq: 880, at: 0.09, dur: 0.16 },
  ];
  for (const n of notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = n.freq;
    gain.gain.setValueAtTime(0.0001, now + n.at);
    gain.gain.exponentialRampToValueAtTime(0.35, now + n.at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + n.at + n.dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + n.at);
    osc.stop(now + n.at + n.dur + 0.02);
  }
}

// Descending "finish reached" cue — richer than the checkpoint chime.
export function playFinishChime() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') { try { ctx.resume(); } catch { /* noop */ } }
  const now = ctx.currentTime;
  const notes = [
    { freq: 880, at: 0, dur: 0.14 },
    { freq: 1174, at: 0.12, dur: 0.14 },
    { freq: 1568, at: 0.24, dur: 0.24 },
  ];
  for (const n of notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = n.freq;
    gain.gain.setValueAtTime(0.0001, now + n.at);
    gain.gain.exponentialRampToValueAtTime(0.4, now + n.at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + n.at + n.dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + n.at);
    osc.stop(now + n.at + n.dur + 0.02);
  }
}

// Best-effort haptics — delegates to the native plugin on iOS/Android (via
// Taptic engine / VibrationEffect) and falls back to navigator.vibrate on
// the web. Silently no-ops where neither is available (desktop Chrome
// without a vibration motor, Safari web, etc.).
export function vibratePattern(pattern) {
  if (isNative()) {
    // Map common web patterns to native impact levels.
    const total = Array.isArray(pattern) ? pattern.reduce((a, b) => a + b, 0) : Number(pattern) || 0;
    const level = total > 300 ? 'heavy' : total > 120 ? 'medium' : 'light';
    nativeHaptic(level);
    return;
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  } catch { /* noop */ }
}
